import { AmcLogo } from "@core/components/logo/amc-logo";
import {
  usePaymentsList,
  useSubmittedOrderById,
  useClient,
  useInvestorBankAccounts,
} from "creso-core/hooks";
import {
  V2Dialog,
  DialogContent,
  DialogTitle,
  V2Loader,
  VisuallyHidden,
} from "web-design-system";
import { formatDate } from "date-fns";
import { convertToCurrency, formatCurrency } from "creso-core/utils";
import { SUBMITTED_ORDER_TYPE_LABELS } from "creso-core/constants";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Route } from "@/routes/__root";
import omit from "lodash/omit";
import Units from "@funds/components/atoms/units";
import { NAV } from "@core/components/nav";
import { OrderStatusBadge } from "web-core";

export function OrderDetailDialog() {
  const router = useRouter();
  const search = useSearch({
    from: Route.id,
  });

  const { client_id: clientId, order_id: orderId } = search;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      if (router.history.canGoBack()) {
        router.history.back();
      } else {
        router.navigate({
          to: ".",
          search: (prev) => omit(prev, ["modal", "client_id", "order_id"]),
          replace: true,
        });
      }
    }
  };

  // Fetch client data
  const { data: client, isLoading: isLoadingClient } = useClient(
    clientId ?? 0,
    !!clientId
  );

  // Fetch order data
  const { data: order, isLoading: isLoadingOrder } = useSubmittedOrderById(
    {
      id: orderId ?? "",
      investment_account_guid: client?.investment_account ?? "",
    },
    {
      enabled: !!orderId && !!client?.investment_account,
    }
  );

  // Fetch all payment details
  const { data: paymentDetails, isLoading: isLoadingPayments } =
    usePaymentsList(
      {
        investment_account_guid: client?.investment_account ?? "",
        order_ids: orderId ? [Number(orderId)] : [],
      },
      {
        enabled: !!orderId && !!client?.investment_account,
      }
    );

  // Fetch bank accounts
  const { data: bankAccounts, isLoading: isLoadingBankAccounts } =
    useInvestorBankAccounts(client?.investment_account ?? "");

  // Filter bank account based on bank_account_id from payment details
  const bankAccount = bankAccounts?.find(
    (account) => account.id === paymentDetails?.[0]?.bank_account_id
  );

  const isLoading =
    isLoadingClient ||
    isLoadingOrder ||
    isLoadingPayments ||
    isLoadingBankAccounts;

  if (!order) {
    return null;
  }

  return (
    <V2Dialog defaultOpen onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <VisuallyHidden>
          <DialogTitle>Recent Order Details</DialogTitle>
        </VisuallyHidden>
        <V2Loader isLoading={isLoading} className="flex flex-col">
          <div className="flex items-center pb-5 gap-4 border-b border-border">
            <AmcLogo size="sm" amc_code={order.amc_morningstar_code} />
            <span className="text-base text-ellipsis overflow-hidden font-semibold mr-auto">
              {order.scheme_name}
            </span>
            <OrderStatusBadge status={order.order_status} />
          </div>

          <div
            className={`py-4 flex flex-col gap-4 ${paymentDetails?.[0] ? "border-b border-border" : ""}`}
          >
            <span className="font-semibold text-sm">Order details</span>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm">Order type</span>
                <span className="text-sm font-semibold">
                  {SUBMITTED_ORDER_TYPE_LABELS[order.type]}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Placed on</span>
                <span className="text-sm font-semibold">
                  {formatDate(new Date(order.created_at), "dd MMM yyyy")} •{" "}
                  {formatDate(new Date(order.created_at), "hh:mm a")}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">
                  {order.gift ? "USD Amount" : "Amount"}
                </span>
                <span className="text-sm font-semibold">
                  {order.gift
                    ? order.gift.amount
                      ? formatCurrency(
                          Number(order.gift.amount ?? 0) -
                            Number(order.gift.transaction_fee ?? 0),
                          "USD",
                          {
                            precision: 0,
                          }
                        )
                      : "-"
                    : order.amount
                      ? convertToCurrency(order.amount)
                      : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Units</span>
                <span className="text-sm font-semibold">
                  {order.units ? <Units stripSuffix>{order.units}</Units> : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">NAV date</span>
                <span className="text-sm font-semibold">
                  {order.nav_date
                    ? formatDate(new Date(order.nav_date ?? ""), "dd MMM yyyy")
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">NAV</span>
                <span className="text-sm font-semibold">
                  {order.gift ? (
                    <NAV className="text-sm" value={order.gift.nav ?? 0} />
                  ) : order.nav ? (
                    <NAV className="text-sm" value={order.nav ?? 0} />
                  ) : (
                    "-"
                  )}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm">Folio no</span>
                <span className="text-sm font-semibold">
                  {order.folio_number || "-"}
                </span>
              </div>
            </div>
          </div>

          {order.gift && (
            <div className="py-4 flex flex-col gap-4 border-t border-border">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm">Transaction fee</span>
                  <span className="text-sm font-semibold">
                    {order.gift.transaction_fee
                      ? formatCurrency(order.gift.transaction_fee, "USD", {
                          precision: 2,
                        })
                      : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm">Total USD Amount</span>
                  <span className="text-sm font-semibold">
                    {order.gift.amount
                      ? formatCurrency(Number(order.gift.amount), "USD", {
                          precision: 0,
                        })
                      : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm">FX rate</span>
                  <span className="text-sm font-semibold">
                    {order.gift.fx_rate ? Number(order.gift.fx_rate) : "-"}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm">INR Invested</span>
                  <span className="text-sm font-semibold">
                    {order.amount
                      ? formatCurrency(order.amount, "INR", {
                          precision: 2,
                        })
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {paymentDetails?.[0] && (
            <div className="py-4 flex flex-col gap-4">
              <span className="font-semibold text-sm">Payment details</span>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-sm">Mode of Payment</span>
                  <span className="text-sm font-semibold">
                    {paymentDetails?.[0]?.mode_of_payment || "RTGS"}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm">Bank</span>
                  <span className="text-sm font-semibold">
                    {bankAccount?.bank_name
                      ? `${bankAccount.bank_name} *${bankAccount.account_number.slice(-4)}`
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </V2Loader>
      </DialogContent>
    </V2Dialog>
  );
}
